import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import dayjs from 'utils/dayjs';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  VitalFieldKey,
  VitalMetric,
  TrendChartPoint,
  VitalsHistoryItem,
} from 'core/vitals/types';
import {
  formatDateTime,
} from 'utils/date';
import {
  DateRange,
} from 'types/dateRange';

export const vitalMetrics: VitalMetric[] = [{
  id: 'blood-pressure',
  viewLabel: 'BP',
  title: 'Blood Pressure',
  unit: 'mmHg',
  series: [{
    key: 'systolicBloodPressureMmhg',
    label: 'Systolic',
    color: '#ff5d8f',
  }, {
    key: 'diastolicBloodPressureMmhg',
    label: 'Diastolic',
    color: '#ffb020',
  }],
}, {
  id: 'heart-rate',
  viewLabel: 'Heart rate',
  title: 'Heart rate',
  unit: 'bpm',
  series: [{ key: 'heartRateBpm', label: 'Heart rate', color: '#7c4dff' }],
}, {
  id: 'respiratory-rate',
  viewLabel: 'Respiratory rate',
  title: 'Respiratory rate',
  unit: 'breaths/min',
  series: [{ key: 'respiratoryRateBreathsPerMinute', label: 'Respiratory rate', color: '#00b8a9' }],
}, {
  id: 'temperature',
  viewLabel: 'Temperature',
  title: 'Temperature',
  unit: '°F',
  series: [{ key: 'temperatureFahrenheit', label: 'Temperature', color: '#ff8a3d' }],
}, {
  id: 'oxygen-saturation',
  viewLabel: 'Oxygen saturation',
  title: 'Oxygen saturation',
  unit: '%',
  series: [{ key: 'oxygenSaturationPercent', label: 'Oxygen saturation', color: '#2f80ff' }],
}];

export const VITALS_EMPTY_STATE_TEXT = Object.freeze({
  noVitalsForDateRange: 'No vitals in the selected date range',
  noTrendDataForDateRange: 'No readings in selected date range',
  noRecordedVitals: 'Nothing recorded yet',
});

export type VitalsDateRange = DateRange;

export function getDefaultVitalsDateRange(): VitalsDateRange {
  const now = dayjs();
  return {
    min: now.subtract(3, 'month').startOf('day').toDate(),
    max: now.endOf('day').toDate(),
  };
}

export type VitalTrend = {
  metric: VitalMetric;
  points: TrendChartPoint[];
};

export function formatVitalNumber(
  value: number,
  key?: VitalFieldKey,
): string {
  return key === 'temperatureFahrenheit' ? value.toFixed(1) : `${Math.round(value)}`;
}

export function formatVitalMetric(
  item: Partial<Record<VitalFieldKey, number | null | undefined>>,
  metric: VitalMetric,
): string {
  return metric.series.map(({ key }) => {
    const value = item[key];

    return value == null ? DEFAULT_EMPTY_VALUE : formatVitalNumber(Number(value), key);
  }).join(metric.series.length > 1 ? ' / ' : '');
}

export function getVitalsHistoryDate(item: VitalsHistoryItem): string {
  return item.encounterDateTime ?? item.updatedOn ?? item.createdOn ?? '';
}

export function formatVitalsHistoryDate(item: VitalsHistoryItem): string {
  const value = item.updatedOn ?? item.createdOn ?? '';

  return value ? formatDateTime(value) : DEFAULT_EMPTY_VALUE;
}

function getEncounterDetails(item: VitalsHistoryItem): string {
  return uniq([item.typeOfVisitDescription, item.reasonForVisit]
    .filter(notEmptyString))
    .join(' · ');
}

export function getVitalTrendPoints(
  items: VitalsHistoryItem[],
  metric: VitalMetric,
): TrendChartPoint[] {
  return items
    .filter((item) => (
      notEmptyString(getVitalsHistoryDate(item)) && metric.series.every(({ key }) => !isNil(item[key]))
    ))
    .map((item) => ({
      fullLabel: formatDateTime(getVitalsHistoryDate(item)),
      encounterDetails: getEncounterDetails(item),
      ...Object.fromEntries(metric.series.map(({ key }) => [key, Number(item[key])])),
    }));
}

export function getVitalTrends(items: VitalsHistoryItem[]): VitalTrend[] {
  return vitalMetrics.map((metric) => ({
    metric,
    points: getVitalTrendPoints(items, metric),
  }));
}

export function hasVitalTrendData(pointsByMetric: TrendChartPoint[][]): boolean {
  return pointsByMetric.some((points) => points.length > 0);
}
